import WrapInputLabel from 'common/components/wrap-input-label';
import { Form, Formik } from 'formik';
import { useEffect } from 'react';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import ApplyButtons from 'components/apply-shared/apply-buttons';
import { useUpdateSinglePlanMutation } from 'redux/api/ciosUsersApi/ciosUsersApi';
import useUserTracker from 'common/utils/user-tracker/useUserTracker';
import FormError from 'common/components/form-error';
import Input from 'common/components/input';
import { extractErrorMsg } from 'utils/errors';
import {
  ApplyFormContainer,
  ApplyFormField,
  ApplyFormLayout,
} from 'common/layouts/apply-form';
import { Flex, Text, Checkbox, Tag } from '@chakra-ui/react';
import ApplyTitle from 'components/apply-shared/apply-title';
import ApplyContent from 'components/apply-shared/apply-content';
import { planStatusEnum, studentStatusEnum, schoolLevelEnum, backgroundTypeEnum, propertyKindEnum, coverageTypeEnum } from 'common/utils/enums';
import { toDatePickerFormat } from 'common/utils/toDatePickerFormat';
import SelectInput from 'common/components/select-input';
import { residencyStatusOptions, schoolLevelOptions } from 'common/utils/selectOptions';
import { isEmpty } from 'common/utils/isEmpty';
import { SchoolInfoHelperLink } from 'components/plans/school-info-helper-modal';

const PlanStepPersonalInfo = ({
  onBackClick,
  stepNumber,
  onNextClick,
  planData,
}) => {
  // HOOKS
  const [
    updatePlan,
    { isLoading: updatePlanLoading, isSuccess: isUpdatePlanSuccess },
  ] = useUpdateSinglePlanMutation();

  const {
    trackFormSubmission,
    trackFormSubmissionSuccess,
    trackFormSubmissionError,
    trackCustomEvent,
    trackPageLoad,
  } = useUserTracker();

  useEffect(() => {
    trackPageLoad('PlanStepPersonalInfo')
  })

  const circumstanceKeyMap = {
    JOB_QUIT_PLANS: 'jobQuitPlans',
    OTHER_MOVE_PLANS: 'otherMovePlans',
    REQUIRES_VISA_INFO: 'requiresVisaInfo',
    FREQUENT_MOVES: 'frequentMoves',
    TERMINATION_HISTORY: 'terminationHistory',
    EVICTION_HISTORY: 'evictionHistory',
    CURRENT_STUDENT: 'currentStudent',
  };

  const circumstanceOptions = [
    { value: circumstanceKeyMap.JOB_QUIT_PLANS, label: 'I have committed to an offer of employment that may require me to relocate before the end of my lease.' },
    { value: circumstanceKeyMap.OTHER_MOVE_PLANS, label: 'I have committed to a purchase offer or lease of a home that may require me to relocate before the end of my lease.' },
    { value: circumstanceKeyMap.REQUIRES_VISA_INFO, label: 'I am not a U.S. citizen or permanent resident.' },
    { value: circumstanceKeyMap.CURRENT_STUDENT, label: 'I am currently enrolled in school.' },
    { value: circumstanceKeyMap.FREQUENT_MOVES, label: 'I have moved 3 or more times in the past 3 years.' },
    { value: circumstanceKeyMap.TERMINATION_HISTORY, label: 'I have terminated more than 2 leases in the past 3 years.' },
    { value: circumstanceKeyMap.EVICTION_HISTORY, label: 'I have been evicted in the past 3 years.' },    
  ];

  // Create option menu for visa selection
  const createResidencyOptionMenu = (objArray) => {
    if (isEmpty(objArray)) return [];

    return objArray
      .filter((opt) => opt.value !== 'citizenPermanent') // Exclude citizen/permanent resident option
      .map(({ label, examples, value }) => ({
        label: (
          <div className='w-full flex gap-2 justify-between items-center'>
            <div className='text-md font-medium'>{label}</div>
            <div className='text-md text-gray-500 hidden gap-2 sm:flex'>
              {examples?.map((example, index) => (
                <Tag
                  key={index}
                  size='sm'
                  colorScheme='gray'
                  className='rounded-full px-3'
                >
                  {example}
                </Tag>
              ))}
            </div>
          </div>
        ),
        value: value,
      }));
  };

  const getInitialCircumstanceValues = () => {
    const defaults = {
      [circumstanceKeyMap.JOB_QUIT_PLANS]: false,
      [circumstanceKeyMap.OTHER_MOVE_PLANS]: false,
      [circumstanceKeyMap.REQUIRES_VISA_INFO]: false,
      [circumstanceKeyMap.FREQUENT_MOVES]: false,
      [circumstanceKeyMap.TERMINATION_HISTORY]: false,
      [circumstanceKeyMap.EVICTION_HISTORY]: false,
      [circumstanceKeyMap.CURRENT_STUDENT]: false,
    };

    if (!planData) return defaults;

    // Determine current student status based on priority:
    // 1. If school status is explicitly set (NOT_STUDENT or CURRENT_STUDENT), use that
    // 2. If no explicit school status, default based on property type
    let showCurrentStudent = false;
    const isNotStudent = planData.school?.status === studentStatusEnum.NOT_STUDENT;
    const isCurrentStudent = planData.school?.status === studentStatusEnum.CURRENT_STUDENT;
    const hasProperty = planData?.property;
    const isStudentHousingProperty = planData?.property?.kind === propertyKindEnum.STUDENT;

    // Priority 1: Check explicit school status
    if (isCurrentStudent) {
      showCurrentStudent = true;
    } else if (isNotStudent) {
      showCurrentStudent = false;
    } else {
      // Priority 2: No explicit school status, use property-based default
      if (hasProperty && isStudentHousingProperty) {
        showCurrentStudent = true; // Default to student for student housing
      } else {
        showCurrentStudent = false; // Default to not student for non-student housing
      }
    }

    return {
      ...defaults,
      [circumstanceKeyMap.JOB_QUIT_PLANS]: planData.jobQuitPlans === true,
      [circumstanceKeyMap.OTHER_MOVE_PLANS]: planData.movePlans === true,
      [circumstanceKeyMap.REQUIRES_VISA_INFO]: planData.residencyStatus && planData.residencyStatus !== 'citizenPermanent',
    // API stores actual count for addressesLived. Original form value was count - 1.
      [circumstanceKeyMap.FREQUENT_MOVES]: planData.rentalHistory?.addressesLived >= 3,
      [circumstanceKeyMap.TERMINATION_HISTORY]: planData.rentalHistory?.terminationHistory === true,
      [circumstanceKeyMap.EVICTION_HISTORY]: planData.rentalHistory?.evictionHistory === true,
      [circumstanceKeyMap.CURRENT_STUDENT]: showCurrentStudent,
    };
  };

  const initialCircumstances = getInitialCircumstanceValues();
  
  // Pre-select "none apply" if school status is explicitly not student and no other circumstances are selected
  const isNoneApplyInitially = planData?.school?.status === studentStatusEnum.NOT_STUDENT && 
    !Object.values(initialCircumstances).some(value => value === true);

  // Helper function to check if form has valid selections
  const hasValidFormSelections = (values) => {
    return values.noneApply || Object.values(circumstanceKeyMap).some(key => values[key]);
  };

  return (
    <Formik
      enableReinitialize
      initialValues={{
        ...initialCircumstances,
        noneApply: isNoneApplyInitially,
        residencyStatus:
          planData?.residencyStatus &&
          planData.residencyStatus !== 'citizenPermanent'
            ? planData.residencyStatus : '',
        visaExpiration: toDatePickerFormat(planData?.visaExpiration) || '',
        schoolLevel: planData?.school?.status === studentStatusEnum.CURRENT_STUDENT ? (planData?.school?.level || '') : '',
        schoolGraduationDate: planData?.school?.status === studentStatusEnum.CURRENT_STUDENT ? (toDatePickerFormat(planData?.school?.graduationDate) || '') : '',
        schoolProgramEndDate: planData?.school?.status === studentStatusEnum.CURRENT_STUDENT ? (toDatePickerFormat(planData?.school?.programEndDate) || '') : '',
      }}
      validationSchema={Yup.object().shape({
        noneApply: Yup.boolean(),
        [circumstanceKeyMap.JOB_QUIT_PLANS]: Yup.boolean(),
        [circumstanceKeyMap.OTHER_MOVE_PLANS]: Yup.boolean(),
        [circumstanceKeyMap.REQUIRES_VISA_INFO]: Yup.boolean(),
        [circumstanceKeyMap.FREQUENT_MOVES]: Yup.boolean(),
        [circumstanceKeyMap.TERMINATION_HISTORY]: Yup.boolean(),
        [circumstanceKeyMap.EVICTION_HISTORY]: Yup.boolean(),
        [circumstanceKeyMap.CURRENT_STUDENT]: Yup.boolean(),
        residencyStatus: Yup.string().when(
          circumstanceKeyMap.REQUIRES_VISA_INFO,
          {
            is: true,
            then: (schema) =>
              schema.required('Please select your residency status').notOneOf(['citizenPermanent'], 'Please select your specific non-citizen status'),
            otherwise: (schema) => schema.nullable(),
          }
        ),
        visaExpiration: Yup.date()
          .nullable() // Step 1: Allow null
          .when(circumstanceKeyMap.REQUIRES_VISA_INFO, {
            is: true,
            // If required, null (which passed the above typeError) will now fail .required()
            // A more specific typeError can be added here if needed, but the base one might suffice.
            then: (schema) => schema.required('Please select when your visa expires'),
            // If not required, it's already nullable. The base typeError handles invalid strings.
            otherwise: (schema) => schema, // Relies on base schema's nullability and typeError
          }),
        schoolLevel: Yup.string()
          .when(circumstanceKeyMap.CURRENT_STUDENT, {
            is: true,
            then: (schema) => schema
              .required('Please select your school level'),
            otherwise: (schema) => schema,
          }),
        schoolGraduationDate: Yup.date()
          .nullable()
          .when(circumstanceKeyMap.CURRENT_STUDENT, {
            is: true,
            then: (schema) => schema
              .required('Please select your expected graduation date'),
            otherwise: (schema) => schema,
          }),
        schoolProgramEndDate: Yup.date()
          .nullable()
          .when(circumstanceKeyMap.CURRENT_STUDENT, {
            is: true,
            then: (schema) => schema
              .required('Please select when your program ends'),
            otherwise: (schema) => schema,
          }),
      }).test('at-least-one-selection', 'Please select at least one option that applies to you, or choose "None of the above" if none apply', function(value) {
        const hasAnyCircumstance = Object.values(circumstanceKeyMap).some(key => value[key]);
        const hasNoneApply = value.noneApply;
        return hasAnyCircumstance || hasNoneApply;
      })}
      onSubmit={(values) => {
        let submissionValues = {
          planId: planData.id,
          status: planStatusEnum.PENDING,
          jobQuitPlans: values[circumstanceKeyMap.JOB_QUIT_PLANS],
          movePlans: values[circumstanceKeyMap.OTHER_MOVE_PLANS],
          residencyStatus: values[circumstanceKeyMap.REQUIRES_VISA_INFO] // if requires visa info is true
            ? values.residencyStatus // use the selected residency status
            : 'citizenPermanent', // otherwise, they are a citizen/permanent resident
          rentalHistory: {
            addressesLived: values[circumstanceKeyMap.FREQUENT_MOVES] ? 3 : 1, // Approximation
            terminationHistory: values[circumstanceKeyMap.TERMINATION_HISTORY],
            evictionHistory: values[circumstanceKeyMap.EVICTION_HISTORY],
          },
          school: {
            status: values[circumstanceKeyMap.CURRENT_STUDENT]
              ? studentStatusEnum.CURRENT_STUDENT
              : studentStatusEnum.NOT_STUDENT,
            // Conditionally add date fields only if student is current
            ...(values[circumstanceKeyMap.CURRENT_STUDENT] && {
              level: values.schoolLevel,
              graduationDate: values.schoolGraduationDate,
              programEndDate: values.schoolProgramEndDate,
            }),
          },
        };

        if (values[circumstanceKeyMap.REQUIRES_VISA_INFO]) {
          submissionValues.visaExpiration = values.visaExpiration;
        }

        // Check if limited coverage should be set as default
        const isCurrentStudent = values[circumstanceKeyMap.CURRENT_STUDENT];
        const hasLimitingDates = isCurrentStudent && (
          values.schoolGraduationDate || 
          values.schoolProgramEndDate || 
          (values[circumstanceKeyMap.REQUIRES_VISA_INFO] && values.visaExpiration)
        );

        if (hasLimitingDates) {
          submissionValues.coverage = {
            leaveAnytime: {
              coverageType: coverageTypeEnum.LIMITED
            }
          };
        }

        trackFormSubmission(submissionValues);
        updatePlan(submissionValues)
          .unwrap()
          .then((data) => {
            onNextClick();
            trackFormSubmissionSuccess(submissionValues);
          })
          .catch((error) => {
            const errMsg = extractErrorMsg(error, 'Update Failed');
            toast.error(errMsg);
            trackFormSubmissionError(submissionValues, errMsg);
          });
      }}
    >
      {({
        handleBlur,
        handleChange,
        setFieldValue,
        values,
        isValid,
        dirty,
        handleSubmit,
      }) => {
        const brand = planData?.property?.brand;
        const isDarkBg =
          brand?.backgroundType === backgroundTypeEnum.BACKGROUND_DARK;
        return (
          <Form
            onSubmit={(e) => e.preventDefault()}
            className='flex flex-col w-full h-full'
          >
            {/* 
              Form validation logic:
              - If form is dirty (user has made changes), check both Formik validation and our custom validation
              - If form is not dirty, only check if user has made a valid selection (avoids Formik's isValid limitation)
            */}
            <ApplyTitle
              title='Purchase Coverage'
              subtitle='Please select all of the following that apply to you or anyone on your lease agreement'
                            buttons={
                  <ApplyButtons
                  loading={updatePlanLoading}
                  stepNumber={stepNumber}
                  isValid={dirty ? (isValid && hasValidFormSelections(values)) : hasValidFormSelections(values)}
                  handleSubmit={handleSubmit}
                  moveOn={!dirty && planData?.school?.status}
                  onNextClick={onNextClick}
                  onBackClick={onBackClick}
                  // backTitle='Activate Later'
                  disableBack={!onBackClick}                
                />
              }
            />

            {/* main content */}
            <ApplyContent>
            {planData?.property && (
              <ApplyFormLayout
                  className={`p-6 rounded-lg border relative overflow-clip ${
                    brand
                      ? 'border-gray-200'
                      : 'border-gray-200 bg-gray-50'
                  }`}
                  style={
                    brand?.bgColor ? { backgroundColor: brand.bgColor } : {}
                  }
                >
                  <Flex className='z-10 relative flex-col sm:flex-row gap-6 sm:gap-8 col-span-2'>
                    {brand && (
                      <img
                        src={
                          isDarkBg && brand?.logoAlt
                            ? brand?.logo
                            : brand?.logo
                        }
                        alt={planData?.property?.name}
                        className='max-w-[140px] max-h-[50px] object-contain'
                      />
                    )}
                    <Flex className='flex-col gap-1'>
                      <Text
                      className={`font-medium text-lg ${
                        isDarkBg ? 'text-white' : 'text-black'
                      }`}
                    >
                      ReLease Coverage at {planData?.property?.name}
                    </Text>
                    <Text
                      className={`text-base ${isDarkBg ? 'text-white opacity-60' : 'text-black opacity-60'}`}
                    >
                      {planData?.property?.address?.streetAddress1},{' '}
                      {planData?.property?.address?.city},{' '}
                      {planData?.property?.address?.state}{' '}
                      {planData?.property?.address?.postalCode}
                    </Text>
                    </Flex>
                  </Flex>
                  {brand?.backgroundHeroImage && (
                    <img
                      src={brand?.backgroundHeroImage}
                      alt={planData?.property?.name}
                      className='absolute inset-0 opacity-15 z-0 object-cover'
                    />
                  )}
                </ApplyFormLayout>
            )}
              {/* container */}
              <ApplyFormContainer>
                <ApplyFormLayout className='pb-6'>
                  <ApplyFormField className='col-span-2'>
                    {/* <WrapInputLabel isRequired>
                      Please select all of the following that apply to you:
                    </WrapInputLabel> */}
                    <Flex className='flex-col gap-3'>
                      {circumstanceOptions.map((option) => (
                        <div key={option.value} className={`border border-gray-200 rounded-md justify-stretch ${
                          values[option.value] ? 'bg-gray-50' : ''
                        }`}>
                          <Checkbox
                            key={option.value}
                            name={option.value}
                            isChecked={values[option.value]}
                            onChange={(e) => {
                              const { name, checked } = e.target;
                              setFieldValue(name, checked);

                              // If a circumstance is checked, uncheck "none apply"
                              if (checked) {
                                setFieldValue('noneApply', false);
                              }
                              // Don't auto-select "none apply" when unchecking - let user make the choice
                            }}
                            onBlur={handleBlur}
                            className={`gap-1.5 px-3.5 py-3`}
                          >
                            <span className='text-base'>{option.label}</span>                    
                          </Checkbox>
                            <Flex className='flex-col w-full items-start'>                              
                              {/* Conditional Visa Expiration Date Section */}
                              {option.value === circumstanceKeyMap.REQUIRES_VISA_INFO && values[circumstanceKeyMap.REQUIRES_VISA_INFO] && (
                                <ApplyFormLayout
                                  className='border-t border-gray-200 p-4 pt-3 pl-11'
                                  onClick={(e) => e.stopPropagation()} // Prevent click from bubbling to checkbox
                                  onTouchStart={(e) => e.stopPropagation()} // Optional: for touch devices
                                >
                                  <Flex className='col-span-2 -mb-2'>
                                    <Text className='font-medium text-base'>Visa Information</Text>
                                  </Flex>
                                  <ApplyFormField small>
                                    <WrapInputLabel isRequired>
                                      Residency Status
                                    </WrapInputLabel>
                                    <SelectInput
                                      placeholderText='Select your residency status'
                                      options={createResidencyOptionMenu(
                                        residencyStatusOptions
                                      )}
                                      value={createResidencyOptionMenu(
                                        residencyStatusOptions
                                      ).find(
                                        (status) => status.value === values.residencyStatus
                                      )}
                                      onChange={(selectedOption) => {
                                        setFieldValue('residencyStatus', selectedOption?.value || '');
                                      }}
                                      onBlur={handleBlur}
                                      name='residencyStatus'
                                    />
                                    <FormError name='residencyStatus' />
                                  </ApplyFormField>
                                  <ApplyFormField small>
                                    <WrapInputLabel isRequired>Visa Expiration Date</WrapInputLabel>
                                    <Input
                                      type='date'
                                      name='visaExpiration'
                                      value={values.visaExpiration}
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                    />
                                    <FormError name='visaExpiration' />
                                  </ApplyFormField>
                                </ApplyFormLayout>
                              )}
                              {/* Conditional School Info Section */}
                              {option.value === circumstanceKeyMap.CURRENT_STUDENT && values[circumstanceKeyMap.CURRENT_STUDENT] && (
                                <ApplyFormLayout
                                  className='border-t border-gray-200 p-4 pt-3 pl-11'
                                  onClick={(e) => e.stopPropagation()} // Prevent click from bubbling to checkbox
                                  onTouchStart={(e) => e.stopPropagation()} // Optional: for touch devices
                                >
                                  <Flex className='col-span-2 mb-1 sm:-mb-2 gap-3 sm:flex-row flex-col sm:items-center sm:justify-between'>
                                    <Text className='font-medium text-base'>School Information</Text>
                                    <Text className='text-sm text-gray-500'>
                                    <SchoolInfoHelperLink />
                                    </Text>
                                  </Flex>
                                  <ApplyFormField>
                                    <WrapInputLabel isRequired>School Level</WrapInputLabel>
                                    <SelectInput
                                      placeholderText='Select your school level'
                                      options={schoolLevelOptions}
                                      value={schoolLevelOptions.find(
                                        (level) => level.value === values.schoolLevel
                                      )}
                                      onChange={(selectedOption) => {
                                        setFieldValue('schoolLevel', selectedOption?.value || '');
                                      }}
                                      onBlur={handleBlur}
                                      name='schoolLevel'
                                    />
                                    <FormError name='schoolLevel' />
                                  </ApplyFormField>
                                  <ApplyFormField small>
                                    <WrapInputLabel isRequired>Expected Graduation Date</WrapInputLabel>
                                    <Input
                                      type='date'
                                      name='schoolGraduationDate'
                                      value={values.schoolGraduationDate}
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                    />
                                    <FormError name='schoolGraduationDate' />
                                  </ApplyFormField>
                                  <ApplyFormField small>
                                    <WrapInputLabel isRequired>Expected Program End Date</WrapInputLabel>
                                    <Input
                                      type='date'
                                      name='schoolProgramEndDate'
                                      value={values.schoolProgramEndDate}
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                    />
                                    <FormError name='schoolProgramEndDate' />
                                  </ApplyFormField>
                                </ApplyFormLayout>
                              )}
                            </Flex>
                        </div>
                      ))}
                      <div className={`border border-gray-200 rounded-md justify-stretch ${
                        values.noneApply ? 'bg-gray-50' : ''
                      }`}>
                        <Checkbox
                          name="noneApply"
                          isChecked={values.noneApply}
                          onChange={(e) => {
                            const { checked } = e.target;
                            setFieldValue('noneApply', checked);
                            if (checked) {
                              Object.values(circumstanceKeyMap).forEach(key => {
                                setFieldValue(key, false);
                              });
                            }
                          }}
                          onBlur={handleBlur}
                          className={`gap-1.5 px-3.5 py-3`}
                        >
                          <span className='text-base'>None of the above apply to me</span>
                        </Checkbox>
                      </div>
                    </Flex>
                    {/* Individual errors can be added per checkbox if needed, but not for the group */}
                  </ApplyFormField>
                </ApplyFormLayout>

              </ApplyFormContainer>
            </ApplyContent>
          </Form>
        );
      }}
    </Formik>
  );
};

export default PlanStepPersonalInfo;

/*
Removed for brevity, but these were the original sections that are now condensed into the multi-select or conditionally shown:

                Pre-meditated events (jobQuitPlans, movePlans) - now part of multi-select
                Residency Status (residencyStatus, visaExpiration) - now part of multi-select & conditional visaExpiration
                Rental History (addressesLived, terminationHistory, evictionHistory) - now part of multi-select
                School Info (status, graduationDate, programEndDate) - now part of multi-select & conditional school dates

Example of a removed RadioGroup for jobQuitPlans:
                  <Flex className='gap-5 col-span-2 justify-between items-center mobile:flex-col mobile:gap-4 mobile:items-start'>
                    <Text className='font-normal text-base'>
                      Have you already signed or otherwise committed to an offer
                      of employment that may require you to prematurely cancel
                      your lease?
                    </Text>
                    <RadioGroup
                      value={
                        values.jobQuitPlans != null
                          ? values.jobQuitPlans.toString()
                          : ''
                      }
                      onChange={(value) => {
                        const booleanValue = value === 'true';
                        setFieldValue('jobQuitPlans', booleanValue);
                      }}
                      name='jobQuitPlans'
                    />
                    <FormError name='jobQuitPlans' />
                  </Flex>

Example of removed school status EnumRadioGroup and conditional date inputs:
                  <Flex className='gap-5 col-span-2 justify-between items-center mobile:flex-col mobile:gap-4 mobile:items-start'>
                    <Text className='text-base'>
                      Are you currently enrolled in school?
                    </Text>
                    <EnumRadioGroup
                      enumOptions={[
                        { label: 'Yes', value: studentStatusEnum.CURRENT_STUDENT },
                        { label: 'No', value: studentStatusEnum.NOT_STUDENT },
                      ]}
                      value={values.school.status || null}
                      onChange={(value) => {
                        setFieldValue('school.status', value);
                      }}
                      name='school.status'
                    />
                    <FormError name='school.status' />
                  </Flex>
                  {values.school.status === studentStatusEnum.CURRENT_STUDENT ? (
                    <>
                      <ApplyFormField small>
                        <WrapInputLabel isRequired>
                          Expected Graduation Date
                        </WrapInputLabel>
                        <Input
                          type='date'
                          name='school.graduationDate'
                          value={values.school.graduationDate}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                        <FormError name='school.graduationDate' />
                      </ApplyFormField>
                      <ApplyFormField small>
                        <WrapInputLabel isRequired>
                          Expected Program End Date
                        </WrapInputLabel>
                        <Input
                          type='date'
                          name='school.programEndDate'
                          value={values.school.programEndDate}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                        <FormError name='school.programEndDate' />
                      </ApplyFormField>
                    </>
                  ) : null}

*/
